/* eslint-disable no-fallthrough, import/no-default-export */
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { Noun, Verb, trackUe } from '@trello/analytics';
import {
  CollapsiblesState,
  createBoardSetNameFromSearch,
  loadBoardsForBoardsMenu,
  OverlayType,
  preloadCreateBoardData,
  setBoardsMenuSelectedBoard,
  setOverlay,
  State,
  toggleAlwaysOpenBoardsMenu,
  toggleBoardsMenu,
  updateSidebarState,
  setBoardsMenuSearchText,
} from 'app/gamma/src/modules';
import { MemberContext } from 'app/gamma/src/modules/context';
import {
  getBoardsMenu,
  getBoardsMenuCategoriesWithBoards,
  getBoardsMenuFilteredBoards,
  getBoardsMenuSearchText,
  getBoardStars,
} from 'app/gamma/src/selectors/boards';
import { getCollapsedDrawerSections } from 'app/gamma/src/selectors/collapsibles';
import { getMyId, isLoggedIn } from 'app/gamma/src/selectors/session';
import { areAnyPopoversOrOverlaysCurrentlyOpen } from 'app/gamma/src/selectors/popovers';
import {
  BoardModel,
  BoardsMenuCategoryModel,
  BoardsMenuSelectedItemModel,
} from 'app/gamma/src/types/models';

import { BoardsMenu } from './boards-menu';
import { BoardsMenuContainerProps, StateProps, DispatchProps } from './types';

class BoardsMenuContainer extends React.Component<BoardsMenuContainerProps> {
  componentDidMount() {
    this.props.loader(this.props);
  }

  componentDidUpdate(prevProps: BoardsMenuContainerProps) {
    this.props.loader(this.props, prevProps);
  }

  render() {
    return (
      <MemberContext.Provider value={{ id: this.props.idMe }}>
        <BoardsMenu {...this.props} />
      </MemberContext.Provider>
    );
  }
}

const mapStateToProps = (state: State): StateProps => {
  const {
    alwaysOpenBoardsMenu,
    headerButtonMouseOver,
    isBoardsMenuOpen,
    loading,
    selectedBoard,
  } = getBoardsMenu(state);
  const searchText = getBoardsMenuSearchText(state);
  const isSearching = !!searchText;
  const categoriesWithBoards = getBoardsMenuCategoriesWithBoards(state);
  const filteredBoards = isSearching
    ? getBoardsMenuFilteredBoards(state)
    : null;
  const collapsables: CollapsiblesState = getCollapsedDrawerSections(state);
  const visibleCategoryBoards:
    | BoardsMenuSelectedItemModel[]
    | null = !isSearching
    ? categoriesWithBoards
        .filter(
          (category: BoardsMenuCategoryModel) =>
            collapsables[category.id] ? collapsables[category.id].open : true,
        )
        .reduce(
          (
            result: BoardsMenuSelectedItemModel[],
            category: BoardsMenuCategoryModel,
          ) => {
            const items: BoardsMenuSelectedItemModel[] = category.boards.map(
              (board: BoardModel) => ({
                id: board.id,
                category: category.id,
                url: board.url,
              }),
            );

            return result.concat(items);
          },
          [],
        )
    : null;

  const visibleFilteredBoards: BoardsMenuSelectedItemModel[] | null =
    isSearching && filteredBoards
      ? filteredBoards.map((board: BoardModel) => ({
          id: board.id,
          category: null,
          url: board.url,
        }))
      : null;

  const visibleBoards = isSearching
    ? visibleFilteredBoards
    : visibleCategoryBoards;

  return {
    activeOverlays: areAnyPopoversOrOverlaysCurrentlyOpen(state),
    alwaysOpenBoardsMenu,
    categoriesWithBoards,
    filteredBoards,
    headerButtonMouseOver,
    idMe: getMyId(state),
    isBoardsMenuOpen,
    isLoggedIn: isLoggedIn(state),
    isSearching: !!searchText,
    loading,
    searchText,
    selectedBoard,
    visibleBoards,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<State>): DispatchProps => ({
  loader({ idMe }, prevProps = { idMe: '' }) {
    if (idMe && idMe !== prevProps.idMe) {
      dispatch(updateSidebarState());
      dispatch(loadBoardsForBoardsMenu(idMe));
      dispatch(preloadCreateBoardData());
    }
  },

  clearSearchText() {
    dispatch(setBoardsMenuSearchText(''));
  },

  clearSelectedBoard() {
    dispatch(setBoardsMenuSelectedBoard(null));
  },

  toggleMenu() {
    dispatch(toggleBoardsMenu());
  },

  showCreateBoard() {
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: Verb.OPENS,
      directObj: Noun.CREATE_BOARD_MODAL,
    });
    dispatch(preloadCreateBoardData());
    dispatch(createBoardSetNameFromSearch());
    dispatch(setOverlay({ overlayType: OverlayType.CreateBoard }));
    dispatch(toggleBoardsMenu());
  },

  toggleAlwaysOpenBoardsMenu(alwaysOpenBoardsMenu) {
    dispatch(toggleAlwaysOpenBoardsMenu());
    dispatch(toggleBoardsMenu());

    trackUe({
      category: Noun.BOARDS_MENU,
      verb: alwaysOpenBoardsMenu ? Verb.DISABLES : Verb.SUBMITS,
      directObj: Noun.ALWAYS_OPEN_MENU_DEFAULT,
    });
  },

  toggleClosedBoardDialog() {
    dispatch(toggleBoardsMenu());
    dispatch(setOverlay({ overlayType: OverlayType.ClosedBoards }));
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: Verb.SHOWS,
      directObj: Noun.CLOSED_BOARDS,
    });
  },

  setSelectedBoard(selected) {
    dispatch(setBoardsMenuSelectedBoard(selected));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  {
    areStatesEqual(next, prev) {
      const {
        isBoardsMenuOpen: prevIsOpen,
        alwaysOpenBoardsMenu: prevAlwaysOpen,
      } = getBoardsMenu(prev);
      const {
        isBoardsMenuOpen: nextIsOpen,
        alwaysOpenBoardsMenu: nextAlwaysOpen,
      } = getBoardsMenu(next);

      switch (true) {
        // If boards or stars were added or removed, update the DOM even if it is hidden
        // This ensures that desktop menu shortcuts still work until desktop can get
        // off of jQuery click magic
        case getBoardStars(prev).length !== getBoardStars(next).length ||
          prev.models.boards.length !== next.models.boards.length:
          return false;

        // Otherwise, if the menu is and will remain hidden…
        case !nextIsOpen && !prevIsOpen && !nextAlwaysOpen && !prevAlwaysOpen:
          // skip re-rendering
          return true;

        // else if any underlying models change…
        case next.models !== prev.models:
        // or if the UI state of the boards menu has changed…
        case next.ui.boardsMenu !== prev.ui.boardsMenu:
        // or if the boards menu popover has opened/closed…
        case nextIsOpen !== prevIsOpen:
          // …assume we need to update.
          return false;

        default:
          // Otherwise, skip re-rendering as building the boards list is expensive
          return true;
      }
    },
  },
)(BoardsMenuContainer);
